/**
 * Acciones de seguimiento de una fila de precios o de cambio.
 *
 * Cada fila tiene dos acciones, con el mismo par de iconos:
 *
 * - **Estación** (`Building2`): da de alta todos los productos que vende esa
 *   estación, o la quita entera del seguimiento si ya estaba.
 * - **Producto** (`Target` para marcar, papelera para quitar): afecta sólo al
 *   producto de la fila.
 *
 * Los iconos no llevan texto para no ensanchar la tabla; el detalle va en el
 * `title` y en el `aria-label` de cada botón.
 */

import { Building2, Target, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';

interface SeguimientoAccionesProps {
  estacion: string;
  producto: string;
  /** La estación ya está en seguimiento (con alguno de sus productos). */
  estacionMarcada: boolean;
  /** Ese producto concreto ya está en seguimiento. */
  productoMarcado: boolean;
  /** Hay una operación en curso: se bloquean los botones. */
  ocupado: boolean;
  onMarcarEstacion: () => void;
  onQuitarEstacion: () => void;
  onMarcarProducto: () => void;
  onQuitarProducto: () => void;
}

export function SeguimientoAcciones({
  estacion,
  producto,
  estacionMarcada,
  productoMarcado,
  ocupado,
  onMarcarEstacion,
  onQuitarEstacion,
  onMarcarProducto,
  onQuitarProducto,
}: SeguimientoAccionesProps) {
  return (
    <div className="flex items-center justify-end gap-1">
      {estacionMarcada ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={onQuitarEstacion}
          disabled={ocupado}
          title="Quitar la estación completa del seguimiento"
          aria-label={`Quitar ${estacion} del seguimiento`}
        >
          <Building2 className="size-3.5 text-rose-600" aria-hidden="true" />
        </Button>
      ) : (
        <Button
          size="sm"
          variant="secondary"
          onClick={onMarcarEstacion}
          disabled={ocupado}
          title="Marcar la estación completa, con todos sus productos"
          aria-label={`Marcar ${estacion} completa en el seguimiento`}
        >
          <Building2 className="size-3.5" aria-hidden="true" />
        </Button>
      )}

      {productoMarcado ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={onQuitarProducto}
          disabled={ocupado}
          title="Quitar sólo este producto del seguimiento"
          aria-label={`Quitar ${producto} de ${estacion} del seguimiento`}
        >
          <Trash2 className="size-3.5 text-rose-600" aria-hidden="true" />
        </Button>
      ) : (
        <Button
          size="sm"
          variant="ghost"
          onClick={onMarcarProducto}
          disabled={ocupado}
          title="Marcar sólo este producto"
          aria-label={`Marcar sólo ${producto} de ${estacion}`}
        >
          <Target className="size-3.5" aria-hidden="true" />
        </Button>
      )}
    </div>
  );
}
